// TreeCheckBoxDropdownItems
import * as React from "react";
// import { ToolButton } from "./toolButton";
import { Checkbox, Divider, ListItemButton, ListItemText } from "@mui/material";
import { Base } from "../../../../../framework/base";
import { IIdTitleNode, IdTitleNode } from "../../../../../models/common/idTitleNode";
import { TreeCheckboxDropdownValueWithComment } from "../TreeCheckboxDropdownValueWithComment";

// ***********************************************************************************************************************
export interface ITreeCheckBoxDropdownChildProp {
    isActive: boolean;
    item: ITreeCheckBoxDropdownItem;
    level: number;
    selectedIds: string[];
    singleSelect: boolean;
    showComment: boolean;
    onToggleItem: (item: ITreeCheckBoxDropdownItem, checked: boolean, hasChildren: boolean) => void;
    onGetTitleSuffix?: (id: string) => React.ReactNode | string;
}

class TreeCheckBoxDropdownChild extends React.Component<ITreeCheckBoxDropdownChildProp, {}> {
    render() {
        const props = this.props;
        const item = props.item;
        const hasChildren = item.hasChildren();
        const selected = props.selectedIds.indexOf(item.id) > -1;
        const disabled = item.disabled || !item.active;
        const titleSuffix = !disabled ? props.onGetTitleSuffix?.(item.id) ?? null : null;
        return (
            <>
                {!props.singleSelect &&
                    <ListItemButton disabled={disabled} sx={{ paddingLeft: `calc(16px + ${props.level * 16}px)`, maxHeight: "42px" }} onClick={(e) => { e.stopPropagation(); e.preventDefault(); props.onToggleItem(item, !selected, hasChildren); }}>
                        <Checkbox
                            title={item.title}
                            checked={selected}
                            disabled={disabled}
                        />
                        <ListItemText>{item.title}{titleSuffix}</ListItemText>
                    </ListItemButton>
                }
                {!!props.singleSelect && !props.showComment &&
                    <ListItemButton disabled={disabled} sx={{ paddingLeft: `calc(16px + ${props.level * 16}px)`, maxHeight: "42px" }} selected={selected} onClick={() => props.onToggleItem(item, true, false)}>
                        <ListItemText>{item.title}{titleSuffix}</ListItemText>
                    </ListItemButton>
                }
                {!!props.singleSelect && !!props.showComment &&
                    <ListItemButton disabled={disabled} sx={{ paddingLeft: `calc(16px + ${props.level * 16}px)`, height: "auto", minHeight: "42px" }} selected={selected} onClick={() => props.onToggleItem(item, true, false)}>
                        <TreeCheckboxDropdownValueWithComment item={item} onGetTitleSuffix={!disabled ? props.onGetTitleSuffix : undefined} />
                    </ListItemButton>
                }
                {item.children.map((childItem) =>
                    <TreeCheckBoxDropdownChild
                        key={childItem.id}
                        isActive={true}
                        item={childItem}
                        level={props.level + 1}
                        selectedIds={props.selectedIds}
                        singleSelect={props.singleSelect}
                        showComment={props.showComment}
                        onToggleItem={props.onToggleItem}
                        onGetTitleSuffix={props.onGetTitleSuffix}
                    />
                )}
            </>
        );
    }
}

export interface ITreeCheckBoxDropdownItem extends IIdTitleNode {
    classes?: string;
    disabled?: boolean;
}

export interface ITreeCheckBoxDropdownItemsProp {
    classes?: string;
    disabled?: boolean;
    selectedIds: string[];
    selectSubSectionValuesByTitle?: boolean;
    singleSelect?: boolean;
    showComment?: boolean;
    toggleAllId?: string;
    toggleNotSetId?: string;
    values: ITreeCheckBoxDropdownItem[];
    onChange: (selectedIds: string[]) => void;
    onToggleDropdown?: () => void;
    onGetTitleSuffix?: (id: string) => React.ReactNode | string;
}

export class TreeCheckBoxDropdownItems extends React.Component<ITreeCheckBoxDropdownItemsProp, {}> {
    private dividerId = "00000000-0000-0000-0000-000000009999";

    getToggleAllId = (): string => {
        return this.props.toggleAllId ?? Base.emptyGuid;
    };

    getToggleNotSetId = (): string => {
        return this.props.toggleNotSetId ?? "00000000-0000-0000-0000-000000001111";
    };

    getSubSectionItemsByItemTitle = (item: ITreeCheckBoxDropdownItem): ITreeCheckBoxDropdownItem[] => {
        if (!item) return [];
        const props = this.props;
        const list = IdTitleNode.getAsList(props.values);
        const index = list.findIndex(i => i.id === item.id);
        if (index < 0) return [];
        for (let i = index; i < index; i++) {
            if (list[i].id === this.dividerId) return [];
        }
        //Is main section item
        const result: ITreeCheckBoxDropdownItem[] = [];
        for (let i = index + 1; i < list.length; i++) {
            if (list[i].title !== item.title) continue;
            result.push(list[i]);
        };
        return result;
    };

    getToggleItemIds = (selectedIds: string[], item: ITreeCheckBoxDropdownItem, add: boolean, hasChildren: boolean, isSubSectionValueSelect: boolean = false): string[] => {
        if (!item) return null;
        const disabled = item.disabled || !item.active;
        if (disabled) return null;
        const props = this.props;
        const id = item.id;
        const allId = this.getToggleAllId();
        const notSetId = this.getToggleNotSetId();
        let result = selectedIds;
        //Handle add/remove item
        let changedIds: string[] = [];
        if (hasChildren) {
            const parent = IdTitleNode.getById(props.values, id);
            if (!parent) return null;
            const childIds = parent.getActiveChildIds();
            result = result.filter(i => i !== allId && i !== notSetId && childIds.indexOf(i) < 0 && i !== id);
            changedIds.push(id);
            changedIds = changedIds.concat(childIds);
            if (add) {
                result = result.concat(changedIds);
            }
        } else {
            if (id === allId) {
                if (add) {
                    return [allId];
                } else if (props.values.length > 1) {
                    const list = IdTitleNode.getAsList(props.values);
                    const newId = list.find(i => i.id !== allId);
                    result = newId ? [newId.id] : [];
                    if (result.length > 0) {
                        changedIds.push(newId.id);
                    }
                }
            } else if (id === notSetId) {
                if (add) {
                    return [notSetId];
                } else if (props.values.length > 0) {
                    const list = IdTitleNode.getAsList(props.values);
                    const newId = list.find(i => i.id !== notSetId);
                    result = newId ? [newId.id] : [];
                    if (result.length > 0) {
                        changedIds.push(newId.id);
                    }
                }
            } else {
                changedIds.push(id);
                if (add) {
                    result = result.filter(i => i !== allId && i !== notSetId);
                    if (result.indexOf(id) < 0) {
                        result.push(id);
                    }
                } else {
                    result = result.filter(i => i !== id);
                }
            }
        }
        if (result.length < 1) {
            result.push(allId);
        }
        //Handle sub section items
        if (changedIds.length > 0 && props.selectSubSectionValuesByTitle && !isSubSectionValueSelect && id !== allId && id !== notSetId) {
            for (const changedId of changedIds) {
                const changedItem = IdTitleNode.getById(props.values, changedId);
                if (!changedItem) continue;
                const subSectionItems = this.getSubSectionItemsByItemTitle(changedItem);
                for (const subSectionItem of subSectionItems) {
                    const subSectionResult = this.getToggleItemIds(result, subSectionItem, add, subSectionItem.hasChildren(), true);
                    if (!subSectionResult) continue;
                    result = subSectionResult;
                }
            }
        }
        return result;
    };

    handleToggleItem = (item: ITreeCheckBoxDropdownItem, checked: boolean, hasChildren: boolean) => {
        const props = this.props;
        const activeItemIds = this.getToggleItemIds(props.selectedIds, item, checked, hasChildren);
        if (!activeItemIds) return;
        this.props.onChange(activeItemIds);
    };

    handleSetSingleItem = (item: ITreeCheckBoxDropdownItem) => {
        const props = this.props;
        if (!item) return;
        const disabled = item.disabled || !item.active;
        if (disabled) return;
        props.onChange([item.id]);
        if (!props.onToggleDropdown) return;
        props.onToggleDropdown();
    };

    render() {
        const props = this.props;
        const singleSelect = !!props.singleSelect;
        return (
            <>
                {props.values.map((item) => {
                    //Divider
                    const isDivider = item.id === this.dividerId;
                    if (isDivider) {
                        const title = item.getTitle();
                        return (
                            <Divider key={item.id + title}>{title}</Divider>
                        );
                    }
                    //Node
                    const hasChildren = item.hasChildren();
                    const selected = props.selectedIds.indexOf(item.id) > -1;
                    const disabled = item.disabled || !item.active;
                    const titleSuffix = !disabled ? props.onGetTitleSuffix?.(item.id) ?? null : null;
                    return <div key={item.id}>
                        {!singleSelect &&
                            <ListItemButton disabled={disabled} onClick={(e) => { e.stopPropagation(); e.preventDefault(); this.handleToggleItem(item, !selected, hasChildren); }} sx={{ maxHeight: "42px" }}>
                                <Checkbox
                                    key={item.id}
                                    title={item.title}
                                    checked={selected}
                                    disabled={disabled}
                                />
                                <ListItemText>{item.title}{titleSuffix}</ListItemText>
                            </ListItemButton>
                        }
                        {singleSelect && !props.showComment &&
                            <ListItemButton disabled={disabled} onClick={() => this.handleSetSingleItem(item)} sx={{ maxHeight: "42px" }} selected={selected}>
                                <ListItemText>{item.title}{titleSuffix}</ListItemText>
                            </ListItemButton>
                        }
                        {singleSelect && !!props.showComment &&
                            <ListItemButton disabled={disabled} onClick={() => this.handleSetSingleItem(item)} sx={{ height: "auto", minHeight: "42px" }} selected={selected}>
                                <TreeCheckboxDropdownValueWithComment item={item} onGetTitleSuffix={!disabled ? props.onGetTitleSuffix : undefined} />
                            </ListItemButton>
                        }
                        {item.children.map((childItem) =>
                            <TreeCheckBoxDropdownChild
                                key={childItem.id}
                                isActive={true}
                                item={childItem}
                                level={1}
                                selectedIds={props.selectedIds}
                                singleSelect={singleSelect}
                                showComment={props.showComment}
                                onToggleItem={!singleSelect ? this.handleToggleItem : (item: ITreeCheckBoxDropdownItem) => this.handleSetSingleItem(item)}
                                onGetTitleSuffix={props.onGetTitleSuffix}
                            />
                        )}
                    </div>;
                })}
            </>
        );
    }
}
